/**
 * Attribution du « résultat 2032 » (PRD §14.1, ADR 0005 §11, ADR 0009 §4) : contribution marginale de chaque mesure adoptée,
 * de chaque décision de crise, du choc subi de chaque événement et du financement aux KPIs de fin de mandat, par contrefactuel.
 * Pur et déterministe. Le texte d'explication est un gabarit rempli par les nombres, sans texte libre.
 *
 * Contrefactuels (v0.3) : la séquence d'événements de la partie réelle (événement, option, issue du risque) est FIGÉE et rejouée
 * à l'identique dans chaque contrefactuel (même graine, flux d'aléa des votes séparé) ; seul l'élément étudié change :
 * - mesure / financement : la même partie sans l'élément ;
 * - décision de crise : le même événement avec l'option de référence (`reference_option_id`) — une décision égale
 *   à la référence a une contribution nulle ;
 * - choc subi (`kind: "shock"`) : l'événement avec l'option de référence, moins le même mandat sans l'événement : ce qu'il coûte
 *   quoi qu'on décide.
 * Limites : les contributions ne s'additionnent pas exactement (interactions : taux, confiance, seuils, censure).
 */
import { param } from "./params.js";
import { referenceOption } from "./politics.js";
import { simulate } from "./simulate.js";
import { indicatorValue } from "./scorecard-values.js";
import type { Baseline, Choices, Financing, ParamSet, Programme, ScheduledEvent, ScorecardIndicatorDef, SimContent, SimOptions, Timeline, YearState } from "./types.js";

/** Options de l'attribution (ADR 0015) : mode réaliste, indicateurs du bilan à attribuer, séquence imposée, chocs omis. */
export interface AttributionOptions {
  realistic?: boolean;
  /** Indicateurs du bilan : chaque item porte aussi `indicators[id]` = Δ de l'indicateur (dans son unité, signe d'affichage). */
  indicators?: ScorecardIndicatorDef[];
  /** Séquence d'événements de la partie « réelle » (sinon : celle que produit `simulate`). */
  schedule?: ScheduledEvent[];
  /** Ne pas calculer les items « choc subi » (inutiles pour l'écart au tendanciel, où le choc est commun). */
  skipShocks?: boolean;
}

/** KPIs suivis par l'attribution (valeurs de fin de mandat ; croissance = moyenne du mandat). */
export interface AttributionKpis {
  deficit_pct_gdp: number;
  debt_pct_gdp: number;
  unemployment_pct: number;
  purchasing_power_index: number;
  growth_avg_pct: number;
}

export type AttributionKind = "measure" | "event" | "shock" | "financing";

export interface AttributionItem {
  kind: AttributionKind;
  /** Id de mesure, id d'événement, ou `financing`. */
  id: string;
  /** Pour un événement : option retenue. */
  option_id?: string;
  /** Pour un événement : option de référence du contrefactuel. */
  reference_option_id?: string;
  label: string;
  /** Partie réelle − contrefactuel sans l'élément (> 0 = l'élément augmente le KPI ; pour le déficit, > 0 = solde amélioré). */
  delta: AttributionKpis;
  /** Raison du game over du contrefactuel, s'il y en a un (et pas dans la partie réelle à la même date). */
  counterfactual_game_over: string | null;
  counterfactual_events: string[];
  /** Explication en une ou deux phrases, générée par gabarit à partir des nombres. */
  rationale: string;
  /** Δ des indicateurs du bilan (ADR 0015), si `options.indicators` est fourni : id → Δ dans l'unité de l'indicateur. */
  indicators?: Record<string, number>;
}

export interface Attribution {
  /** Année de référence (dernière année simulée de la partie réelle). */
  year: number;
  final: AttributionKpis;
  game_over: Timeline["game_over"];
  items: AttributionItem[];
}

function kpisAt(t: Timeline, year: number): AttributionKpis | null {
  if (t.years.length === 0) return null;
  const upto = t.years.filter((y) => y.year <= year);
  const last: YearState | undefined = upto[upto.length - 1];
  if (!last) return null;
  const k = (key: string): number => last.kpis[key] ?? NaN;
  return {
    deficit_pct_gdp: k("deficit_pct_gdp"),
    debt_pct_gdp: k("debt_pct_gdp"),
    unemployment_pct: k("unemployment_pct"),
    purchasing_power_index: k("purchasing_power_index"),
    growth_avg_pct: upto.reduce((a, y) => a + (y.kpis["growth_pct"] ?? 0), 0) / upto.length,
  };
}

const KEYS: (keyof AttributionKpis)[] = ["deficit_pct_gdp", "debt_pct_gdp", "unemployment_pct", "purchasing_power_index", "growth_avg_pct"];

/** Phrase par KPI, signe compris (gabarit fermé). */
function phrase(key: keyof AttributionKpis, d: number): string {
  const a = Math.abs(d).toFixed(1).replace(".", ",");
  switch (key) {
    case "deficit_pct_gdp":
      return d >= 0 ? `améliore le solde public de ${a} pt de PIB` : `creuse le déficit de ${a} pt de PIB`;
    case "debt_pct_gdp":
      return d >= 0 ? `alourdit la dette de ${a} pt de PIB` : `allège la dette de ${a} pt de PIB`;
    case "unemployment_pct":
      return d >= 0 ? `ajoute ${a} pt de chômage` : `retire ${a} pt de chômage`;
    case "purchasing_power_index":
      return d >= 0 ? `augmente le pouvoir d'achat de ${a} %` : `réduit le pouvoir d'achat de ${a} %`;
    case "growth_avg_pct":
      return d >= 0 ? `ajoute ${a} pt de croissance par an` : `retire ${a} pt de croissance par an`;
  }
}

/**
 * Explication par gabarit. Les KPIs sont classés par |Δ| / `attribution_scale_<kpi>` (paramètres d'affichage : quelle variation
 * de chaque KPI « pèse » autant qu'un point de déficit) ; sous `attribution_negligible`, un Δ n'est pas cité.
 */
export function rationale(
  params: ParamSet,
  kind: AttributionKind,
  label: string,
  delta: AttributionKpis,
  year: number,
  cfGameOver: string | null,
  referenceLabel?: string,
): string {
  const noun = kind === "measure" ? "cette mesure" : kind === "event" ? "cette décision de crise" : kind === "shock" ? "cet événement" : "ce financement";
  const versus =
    kind === "event" && referenceLabel !== undefined
      ? `par rapport à l'option « ${referenceLabel} »`
      : kind === "shock"
        ? "par rapport au même mandat sans cet événement"
        : `par rapport au même mandat sans ${noun}`;
  const scale = (k: keyof AttributionKpis): number => param(params, `attribution_scale_${k}`);
  const eps = param(params, "attribution_negligible");
  const ranked = [...KEYS].filter((k) => Number.isFinite(delta[k])).sort((a, b) => Math.abs(delta[b]) / scale(b) - Math.abs(delta[a]) / scale(a));
  const significant = ranked.filter((k) => Math.abs(delta[k]) >= eps);
  const head = `« ${label} »`;
  const gameOver = cfGameOver
    ? kind === "event" && referenceLabel !== undefined
      ? ` Avec l'option « ${referenceLabel} », le mandat se serait interrompu (${cfGameOver}).`
      : ` Sans ${noun}, le mandat se serait interrompu (${cfGameOver}).`
    : "";
  if (significant.length === 0) return `${head} : effet négligeable sur les grands indicateurs en ${year}.${gameOver}`;
  const [first, second] = significant;
  const parts = [phrase(first!, delta[first!])];
  if (second) parts.push(phrase(second, delta[second]));
  return `${head} ${parts.join(" et ")} en ${year}, ${versus}.${gameOver}`;
}

/**
 * Contributions marginales au résultat de fin de mandat. Mêmes arguments que `simulate` ; `content` est requis (catalogue
 * des mesures et événements, pour rejouer et nommer les éléments).
 */
export function attribute2032(
  baseline: Baseline,
  params: ParamSet,
  programme: Programme,
  financing: Financing,
  choices: Choices,
  seed: number,
  content: SimContent,
  options: AttributionOptions = {},
): Attribution {
  const simOpts = (sched?: ScheduledEvent[]): SimOptions => ({ ...(options.realistic ? { realistic: true } : {}), ...(sched ? { schedule: sched } : {}) });
  const actual = simulate(baseline, params, programme, financing, choices, seed, content, simOpts(options.schedule));
  const lastYear = actual.years[actual.years.length - 1];
  if (!lastYear) throw new Error("attribute2032 : aucune année simulée (censure au premier budget)");
  const year = lastYear.year;
  const final = kpisAt(actual, year) as AttributionKpis;
  const items: AttributionItem[] = [];
  // Séquence figée des événements de la partie réelle (ADR 0009 §4).
  const schedule: ScheduledEvent[] = actual.years.flatMap((y, i) =>
    y.events_fired.map((id) => ({ turn: i + 1, event_id: id, option_id: y.choices_applied?.[id] ?? "", risk_hit: y.risks_hit?.[id] ?? false })),
  );
  const replay = (p: Programme, f: Financing, sched: ScheduledEvent[]): Timeline => simulate(baseline, params, p, f, choices, seed, content, simOpts(sched));
  const defs = options.indicators;

  const add = (kind: AttributionKind, id: string, label: string, plus: Timeline, minus: Timeline | null, extra: { optionId?: string; refId?: string; refLabel?: string } = {}): void => {
    // Δ = « avec l'élément » − « sans l'élément » ; `plus` = la partie réelle sauf pour le choc subi.
    const kp = plus === actual ? final : kpisAt(plus, year);
    const km = minus ? kpisAt(minus, year) : null;
    const delta = {} as AttributionKpis;
    for (const key of KEYS) delta[key] = kp && km ? kp[key] - km[key] : 0;
    let ind: Record<string, number> | undefined;
    if (defs) {
      ind = {};
      for (const d of defs) {
        const a = indicatorValue(d, plus, year);
        const b = minus ? indicatorValue(d, minus, year) : null;
        ind[d.id] = a !== null && b !== null ? a - b : 0;
      }
    }
    const cf = minus ?? plus;
    const cfOver = cf.game_over && (!actual.game_over || cf.game_over.year < actual.game_over.year) ? cf.game_over.reason : null;
    items.push({
      kind,
      id,
      ...(extra.optionId !== undefined ? { option_id: extra.optionId } : {}),
      ...(extra.refId !== undefined ? { reference_option_id: extra.refId } : {}),
      label,
      delta,
      counterfactual_game_over: cfOver,
      counterfactual_events: cf.years.flatMap((y) => y.events_fired),
      rationale:
        kind === "event" && extra.optionId !== undefined && extra.optionId === extra.refId
          ? `« ${label} » : option de référence de cet événement ; ses effets sont comptés dans « Choc subi ».`
          : rationale(params, kind, label, delta, year, cfOver, extra.refLabel),
      ...(ind ? { indicators: ind } : {}),
    });
  };

  for (const id of programme.measure_ids) {
    const m = content.measures.find((x) => x.id === id);
    if (!m) throw new Error(`attribute2032 : mesure inconnue « ${id} »`);
    const hyp = { ...programme.hyp };
    delete hyp[id];
    const variants = { ...(programme.variants ?? {}) };
    delete variants[id];
    const cf = replay({ ...programme, measure_ids: programme.measure_ids.filter((x) => x !== id), hyp, variants }, financing, schedule);
    add("measure", id, m.label, actual, cf);
  }

  for (const x of schedule) {
    const e = content.events.find((ev) => ev.id === x.event_id);
    if (!e) continue;
    const opt = e.options.find((o) => o.id === x.option_id);
    const ref = referenceOption(e);
    // Option retenue = référence : le rejeu avec la référence EST la partie réelle (même séquence), inutile de le recalculer.
    const withRef = opt?.id === ref.id ? actual : replay(programme, financing, schedule.map((y) => (y === x ? { ...y, option_id: ref.id, risk_hit: ref.risk ? y.risk_hit : false } : y)));
    // Décision : option retenue vs option de référence (contribution nulle si c'est la référence).
    add("event", x.event_id, opt ? `${e.title} — ${opt.label}` : e.title, actual, opt?.id === ref.id ? actual : withRef, { optionId: x.option_id, refId: ref.id, refLabel: ref.label });
    // Choc subi : l'événement (option de référence) vs pas d'événement.
    if (options.skipShocks) continue;
    const without = replay(programme, financing, schedule.filter((y) => y !== x));
    add("shock", x.event_id, `Choc subi : ${e.title}`, withRef, without, { refId: ref.id });
  }

  const hasFinancing =
    Object.values(financing.revenue_raises).some((v) => v !== 0) ||
    financing.across_the_board_cut !== 0 ||
    Object.values(financing.mission_cuts ?? {}).some((v) => v !== 0);
  if (hasFinancing) {
    const cf = replay(programme, { revenue_raises: {}, across_the_board_cut: 0 }, schedule);
    add("financing", "financing", "Financement (recettes nouvelles, économies par mission et rabot)", actual, cf);
  }

  return { year, final, game_over: actual.game_over, items };
}
